import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import {
    AlertTriangle,
    ArrowUpRight,
    Check,
    Copy,
    ExternalLink,
    Loader2,
    Share2,
    Sparkles,
    Trophy,
    Users,
    Wallet,
} from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetHrEarningsSummaryQuery, useGetHrMeQuery, useGetHrReferralsQuery } from '@/store/api/apiSlice';
import type { HrReferral, HrReferralPipelineStage } from '@/types';

function formatCurrency(amount: number | undefined) {
    if (amount === undefined || amount === null || isNaN(amount)) return '₦0';
    return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: 'NGN',
        minimumFractionDigits: 0,
    }).format(amount);
}

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
}

const PIPELINE_LABELS: Record<HrReferralPipelineStage, { label: string; className: string }> = {
    invited: { label: 'Invited', className: 'bg-gray-100 text-gray-700 border-gray-200' },
    signed_up: { label: 'Signed up', className: 'bg-blue-50 text-blue-700 border-blue-100' },
    first_investigation: {
        label: 'First check',
        className: 'bg-amber-50 text-amber-700 border-amber-100',
    },
    active: { label: 'Active', className: 'bg-green-50 text-green-700 border-green-100' },
};

function PipelineChip({ stage }: { stage: HrReferralPipelineStage }) {
    const meta = PIPELINE_LABELS[stage] ?? PIPELINE_LABELS.signed_up;
    return (
        <Badge variant="outline" className={`${meta.className} px-3 py-1 rounded-lg font-medium`}>
            {meta.label}
        </Badge>
    );
}

function ShareCard({
    referralCode,
    shareUrl,
    isLoading,
}: {
    referralCode: string | null;
    shareUrl: string | null;
    isLoading: boolean;
}) {
    const [copied, setCopied] = useState<'code' | 'url' | null>(null);

    const handleCopy = async (value: string, kind: 'code' | 'url') => {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(kind);
            setTimeout(() => setCopied(null), 1800);
        } catch {
            // Clipboard rejected — silent (no destructive fallback).
        }
    };

    const whatsappHref = shareUrl
        ? `https://wa.me/?text=${encodeURIComponent(
              `Run trusted background checks on your candidates with YourChecka. Sign up with my link to claim 5% off your first check: ${shareUrl}`,
          )}`
        : '#';

    const qrSrc = shareUrl
        ? `https://api.qrserver.com/v1/create-qr-code/?size=160x160&data=${encodeURIComponent(shareUrl)}`
        : '';

    return (
        <div className="relative overflow-hidden rounded-2xl md:rounded-3xl bg-[#14171a] text-white p-6 md:p-8">
            <div className="absolute -right-12 -top-12 h-44 w-44 rounded-full bg-[#f37b23]/20 blur-3xl pointer-events-none" />
            <div className="absolute -left-16 -bottom-16 h-44 w-44 rounded-full bg-[#f37b23]/10 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col lg:flex-row items-start gap-6 lg:gap-10">
                <div className="flex-1 w-full">
                    <div className="flex items-center gap-2 mb-3">
                        <Share2 className="h-4 w-4 text-[#f37b23]" />
                        <span className="text-xs uppercase tracking-[0.18em] text-white/60">Your referral link</span>
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2">Share. Refer. Earn.</h2>
                    <p className="text-sm text-white/70 mb-6 max-w-lg">
                        Every employer who signs up with your link gets 5% off — and you earn a commission on every paid
                        investigation they run.
                    </p>

                    {isLoading ? (
                        <div className="flex items-center gap-2 text-white/60 text-sm">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Loading your referral link...
                        </div>
                    ) : referralCode && shareUrl ? (
                        <div className="space-y-3">
                            {/* Code */}
                            <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-wider text-white/50 mb-0.5">Code</p>
                                    <p className="font-mono text-lg font-semibold truncate">{referralCode}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopy(referralCode, 'code')}
                                    className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-xs font-medium transition-colors"
                                >
                                    {copied === 'code' ? (
                                        <>
                                            <Check className="h-3.5 w-3.5" /> Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="h-3.5 w-3.5" /> Copy
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Share URL */}
                            <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-3">
                                <div className="flex-1 min-w-0">
                                    <p className="text-[11px] uppercase tracking-wider text-white/50 mb-0.5">
                                        Share link
                                    </p>
                                    <p className="text-sm truncate text-white/90">{shareUrl}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleCopy(shareUrl, 'url')}
                                    className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 px-3 py-2 text-xs font-medium transition-colors"
                                >
                                    {copied === 'url' ? (
                                        <>
                                            <Check className="h-3.5 w-3.5" /> Copied
                                        </>
                                    ) : (
                                        <>
                                            <Copy className="h-3.5 w-3.5" /> Copy
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* CTAs */}
                            <div className="flex flex-wrap items-center gap-3 pt-2">
                                <a
                                    href={whatsappHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg bg-[#25D366] hover:bg-[#1ebd5b] px-4 py-2.5 text-sm font-medium text-white transition-colors"
                                >
                                    Share on WhatsApp
                                </a>
                                <a
                                    href={shareUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-lg border border-white/15 hover:bg-white/5 px-4 py-2.5 text-sm font-medium text-white transition-colors"
                                >
                                    Preview link
                                </a>
                            </div>
                        </div>
                    ) : (
                        <p className="text-sm text-white/60">
                            Your referral link couldn't be loaded. Please refresh the page.
                        </p>
                    )}
                </div>

                {/* QR Code */}
                {shareUrl && !isLoading && (
                    <div className="shrink-0 self-stretch lg:self-auto flex flex-col items-center justify-center w-full lg:w-auto">
                        <div className="rounded-2xl bg-white p-3 shadow-lg">
                            <img src={qrSrc} alt="Referral QR code" className="h-[160px] w-[160px] object-contain" />
                        </div>
                        <p className="text-[11px] text-white/50 mt-2">Scan to sign up</p>
                    </div>
                )}
            </div>
        </div>
    );
}

function StatCard({
    label,
    value,
    hint,
    icon: Icon,
    tone = 'default',
    cta,
}: {
    label: string;
    value: string;
    hint?: string;
    icon: React.ComponentType<{ className?: string }>;
    tone?: 'default' | 'green' | 'amber' | 'orange';
    cta?: { label: string; onClick: () => void; disabled?: boolean };
}) {
    const tones = {
        default: { card: 'bg-gray-50', iconBg: 'bg-gray-100', iconColor: 'text-gray-600', text: 'text-gray-900' },
        green: { card: 'bg-green-50', iconBg: 'bg-green-100', iconColor: 'text-green-600', text: 'text-green-900' },
        amber: { card: 'bg-amber-50', iconBg: 'bg-amber-100', iconColor: 'text-amber-600', text: 'text-amber-900' },
        orange: { card: 'bg-orange-50', iconBg: 'bg-orange-100', iconColor: 'text-[#f37b23]', text: 'text-gray-900' },
    }[tone];

    return (
        <div className={`rounded-2xl p-4 md:p-5 ${tones.card} flex flex-col`}>
            <div className="flex items-center gap-3 mb-3">
                <div className={`h-10 w-10 rounded-lg ${tones.iconBg} flex items-center justify-center`}>
                    <Icon className={`h-5 w-5 ${tones.iconColor}`} />
                </div>
                <p className="text-xs md:text-sm text-gray-600 font-medium">{label}</p>
            </div>
            <p className={`text-2xl md:text-3xl font-bold ${tones.text}`}>{value}</p>
            {hint && <p className="text-[11px] text-gray-500 mt-1.5">{hint}</p>}
            {cta && (
                <div className="mt-4">
                    <Button
                        onClick={cta.onClick}
                        disabled={cta.disabled}
                        className="w-full bg-gray-900 hover:bg-gray-800 text-white rounded-xl py-2.5 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {cta.label}
                    </Button>
                </div>
            )}
        </div>
    );
}

function ReferralRow({ referral }: { referral: HrReferral }) {
    const name = `${referral.firstName} ${referral.lastName}`.trim() || referral.email;
    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
            <td className="px-4 py-4">
                <div className="flex items-center gap-3">
                    <div className="h-9 w-9 rounded-full bg-gray-900 text-white text-xs font-semibold flex items-center justify-center">
                        {getInitials(referral.firstName, referral.lastName)}
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
                        <p className="text-xs text-gray-500 truncate">{referral.email}</p>
                    </div>
                </div>
            </td>
            <td className="px-4 py-4 text-sm text-gray-700">
                {referral.companyName || <span className="text-gray-400">—</span>}
            </td>
            <td className="px-4 py-4">
                <PipelineChip stage={referral.pipelineStage} />
            </td>
            <td className="px-4 py-4 text-sm text-gray-700">{referral.investigationCount}</td>
            <td className="px-4 py-4 text-sm text-gray-900 font-medium">{formatCurrency(referral.totalCommission)}</td>
            <td className="px-4 py-4 text-sm text-green-700 font-medium">{formatCurrency(referral.paidCommission)}</td>
            <td className="px-4 py-4 text-xs text-gray-500">
                {new Date(referral.signedUpAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                })}
            </td>
        </tr>
    );
}

export default function HrDashboard() {
    const navigate = useNavigate();
    const currentUser = useAppSelector(selectCurrentUser);
    const referralActive = currentUser?.referralActive !== false;

    const { data: me, isLoading: meLoading } = useGetHrMeQuery();
    const { data: referrals = [], isLoading: referralsLoading } = useGetHrReferralsQuery();
    const { data: summary, isLoading: summaryLoading } = useGetHrEarningsSummaryQuery();

    // Available to withdraw = paid commissions minus everything not yet completed in withdrawals.
    // The /withdrawals endpoints are not duplicated here; the Withdraw CTA routes there.
    const lifetimeGross = summary?.lifetimeGross ?? 0;
    const paid = summary?.paid ?? 0;
    const pending = summary?.pending ?? 0;
    const minPayoutAmount = summary?.minPayoutAmount ?? 0;
    const activeReferrals = useMemo(() => referrals.filter((r) => r.pipelineStage === 'active').length, [referrals]);

    const canWithdraw = paid >= minPayoutAmount;

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                {/* <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'HR Partner'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'HR'}
                    only={true}
                    className="border-b border-gray-100"
                /> */}

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6">
                    {/* Welcome */}
                    <div>
                        <p className="text-sm text-gray-400">Welcome back</p>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
                            {currentUser?.firstName ? `Hi ${currentUser.firstName},` : 'HR Partner Dashboard'}
                        </h1>
                        <p className="text-sm text-gray-600 mt-1">
                            Track your referrals, watch your commissions and grow your network.
                        </p>
                    </div>

                    {/* Paused-access banner */}
                    {!referralActive && (
                        <div className="flex items-start gap-3 rounded-xl border border-red-100 bg-red-50 p-4">
                            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                            <div>
                                <p className="text-sm font-semibold text-red-800">
                                    Your referral program access has been paused.
                                </p>
                                <p className="text-xs text-red-700 mt-0.5">
                                    Please contact support to reactivate. New employer signups won't be attributed to
                                    you while access is paused.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Share card — hidden when paused */}
                    {referralActive && (
                        <ShareCard
                            referralCode={me?.referralCode ?? null}
                            shareUrl={me?.shareUrl ?? null}
                            isLoading={meLoading}
                        />
                    )}

                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <StatCard
                            label="Available"
                            value={summaryLoading ? '...' : formatCurrency(paid)}
                            hint={
                                minPayoutAmount
                                    ? `Minimum payout ${formatCurrency(minPayoutAmount)}`
                                    : 'Ready to withdraw'
                            }
                            icon={Wallet}
                            tone="green"
                            cta={{
                                label: 'Withdraw',
                                onClick: () => navigate('/withdrawals'),
                                disabled: !canWithdraw || !referralActive,
                            }}
                        />
                        <StatCard
                            label="Pending"
                            value={summaryLoading ? '...' : formatCurrency(pending)}
                            hint="Clears when admin validates the report"
                            icon={Sparkles}
                            tone="amber"
                        />
                        <StatCard
                            label="Lifetime"
                            value={summaryLoading ? '...' : formatCurrency(lifetimeGross)}
                            hint={`Across ${summary?.counts?.paid ?? 0} paid + ${summary?.counts?.pending ?? 0} pending`}
                            icon={ArrowUpRight}
                            tone="orange"
                        />
                        <StatCard
                            label="Active employers"
                            value={referralsLoading ? '...' : String(activeReferrals)}
                            hint={`${referrals.length} total referred`}
                            icon={Users}
                        />
                    </div>

                    {/* Referrals table / empty state */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 bg-white">
                            <div>
                                <h2 className="text-base md:text-lg font-semibold text-gray-900">Your referrals</h2>
                                <p className="text-xs text-gray-500">Every employer who signed up using your link.</p>
                            </div>
                            <div className="flex items-center gap-2">
                                <Button
                                    onClick={() => navigate('/hr/earnings')}
                                    className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2 rounded-lg text-sm font-medium"
                                >
                                    View earnings
                                </Button>
                                <Button
                                    onClick={() => navigate('/hr/leaderboard')}
                                    className="bg-gray-900 hover:bg-gray-800 text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                                >
                                    <Trophy className="h-4 w-4" />
                                    Leaderboard
                                </Button>
                            </div>
                        </div>

                        {referralsLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : referrals.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <div className="h-14 w-14 rounded-full bg-orange-50 flex items-center justify-center mb-4">
                                    <Users className="h-7 w-7 text-[#f37b23]" />
                                </div>
                                <h3 className="text-base font-semibold text-gray-900 mb-1">No referrals yet</h3>
                                <p className="text-sm text-gray-500 max-w-md mb-5">
                                    Share your referral link with employers to start earning. The first signup unlocks
                                    your pipeline.
                                </p>
                                {me?.shareUrl && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(me.shareUrl).catch(() => {});
                                        }}
                                        className="inline-flex items-center gap-2 rounded-lg bg-gray-900 hover:bg-gray-800 text-white px-4 py-2.5 text-sm font-medium"
                                    >
                                        <Copy className="h-4 w-4" />
                                        Copy your link
                                    </button>
                                )}
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[720px]">
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Employer</th>
                                            <th className="px-4 py-3">Company</th>
                                            <th className="px-4 py-3">Stage</th>
                                            <th className="px-4 py-3">Investigations</th>
                                            <th className="px-4 py-3">Commission</th>
                                            <th className="px-4 py-3">Paid out</th>
                                            <th className="px-4 py-3">Signed up</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {referrals.map((r) => (
                                            <ReferralRow key={r.id} referral={r} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
